import Config from "@/const/Config";
import { SearchKoyouKeiyakuReq, SearchKoyouKeiyakuRes, KoyouKeiyakuOption, KoyouKeiyakuAgreementReq, KeiyakuDetailWithSyuugyouBasyoAndBuka } from "@/types/KoyouKeiyaku";
import { KoyouKeiyakuDetail } from "@/types/KoyouKeiyakuDetail";
import { BaseApi } from "./BaseApi";
import { BaseOptionWithoutCode } from "@/types/Option";
import { Syain } from "@/types/Syain";

export class KoyouKeiyakuApi extends BaseApi {
  API_PREFIX = '/koyoukeiyaku/'

  /**
   * 検索条件に一致した雇用契約書の情報を取得します
   * @param req
   * @returns SearchKoyouKeiyakuRes
   */
  public async searchKeiyaku(req: SearchKoyouKeiyakuReq): Promise<SearchKoyouKeiyakuRes[]> {
    try {
      const res = await this.post<SearchKoyouKeiyakuRes[]>(Config.API_URL + this.API_PREFIX + `search`, req);
      return res.data;
    } catch (e: any) {
      throw e;
    }
  }

  /**
   * 指定された雇用契約書IDから、雇用契約情報取得APIを実行し結果を返します
   * @param id 
   * @returns 
   */
  public async getDetail(id: number): Promise<KoyouKeiyakuDetail> {
    try {
      const res = await this.post<KoyouKeiyakuDetail>(Config.API_URL + this.API_PREFIX + `detail`, { targetId: id })
      // 返却されたオブジェクトからクラスメンバーへ割り当てる
      const resToClassObject = new KoyouKeiyakuDetail();
      Object.assign(resToClassObject, res.data);
      return resToClassObject;

    } catch (e: any) {
      throw e;
    }
  }

  /**
  * 雇用契約書をダウンロードします
  * @returns
  */
  public async downLoad(id: number): Promise<any> {
    try {
      return await this.post(Config.API_URL + this.API_PREFIX + `print`, {
        responseType: "blob",
        targetId: id
        // responseType: "arraybuffer",
      });
    } catch (e: any) {
      throw e;
    }
  }

  /**
   * 雇用契約書を作成し、登録内容を返します
   * @param keiyakuiDetail 
   * @returns 
   */
  public async upsert(keiyakuiDetail: KoyouKeiyakuDetail): Promise<KoyouKeiyakuDetail> {
    try {
      const res = await this.post<KoyouKeiyakuDetail>(Config.API_URL + this.API_PREFIX + `upsert`, keiyakuiDetail)
      return res.data
    } catch (e: any) {
      throw e;
    }
  }

  public async getAllOptions() {
    try {
      const res = await this.get<KoyouKeiyakuOption>(Config.API_URL + '/koyoukeiyaku/options/all')
      return res.data
    } catch (e: any) {
      throw e
    }
  }

  /**
 * 雇用契約書を提出します
 * @param number
 * @returns
 */
  public async submit(id: number): Promise<boolean> {
    try {
      const res = await this.post<boolean>(Config.API_URL + this.API_PREFIX + `submit`, { targetId: id })
      return res.data;
    } catch (e: any) {
      throw e;
    }
  }

  /**
   * 社内承認において承認者が承認処理を行います
   * @param params
   * @returns
   */
  public async approval(id: number, result: number, notes: string | null): Promise<boolean> {
    try {
      let req = {
        targetId: id,
        statusType: result,
        statusNotes: notes,
      }
      const res = await this.post<boolean>(Config.API_URL + this.API_PREFIX + `approval`, req);
      return res.data;
    } catch (e: any) {
      throw e;
    }
  }

  /**
  * 社内承認において確認者が確認処理を行います
  * @param params
  * @returns
  */
  public async check(id: number, notes: string | null): Promise<boolean> {
    try {
      let req = {
        targetId: id,
        statusNotes: notes,
      }
      const res = await this.post<boolean>(Config.API_URL + this.API_PREFIX + `check`, req);
      return res.data;
    } catch (e: any) {
      throw e;
    }
  }

  /**
   * お知らせを既読にします
   * @param params
   * @returns
   */
  public async readDeniedMessage(id: number): Promise<boolean> {
    try {
      const res = await this.post<boolean>(Config.API_URL + this.API_PREFIX + `read_denied_message`, { targetId: id })
      return res.data
    } catch (e: any) {
      throw e;
    }
  }

  /**
 * 雇用契約書を削除します
 * @param number
 * @returns
 */
  public async deleteKoyouKeiyaku(id: number): Promise<boolean> {
    try {
      const res = await this.post<boolean>(Config.API_URL + this.API_PREFIX + `delete`, { targetId: id })
      return res.data;
    } catch (e: any) {
      throw e;
    }
  }

  /**
 * 雇用契約書を編集します
 * @param number
 * @returns
 */
  public async modifyKoyouKeiyaku(koyouKeiyakuDetail: KoyouKeiyakuDetail): Promise<boolean> {
    try {
      const res = await this.post<boolean>(Config.API_URL + this.API_PREFIX + `modify`, { modifyData: koyouKeiyakuDetail })
      return res.data;
    } catch (e: any) {
      throw e;
    }
  }

  /**
   * 指定された雇用契約書IDから、雇用契約情報取得APIを実行し結果を返します
   * @param id 
   * @returns 
   */
  public async findAllApprovedByUserId(userId: number) {
    try {
      const res = await this.post<KeiyakuDetailWithSyuugyouBasyoAndBuka[]>(Config.API_URL + this.API_PREFIX + `approved_by_user_id`, { userId });
      return res.data;
    } catch (e: any) {
      throw e;
    }
  }

  /**
   * 指定ユーザーの雇用契約のうち、内容変更が可能な雇用契約の取得
   * @param id 
   * @returns 
   */
  public async findAllChangeableKoyouKeiyaku(userId: number) {
    try {
      const res = await this.post<KeiyakuDetailWithSyuugyouBasyoAndBuka[]>(Config.API_URL + this.API_PREFIX + `changeable_list`, { userId });
      return res.data;
    } catch (e: any) {
      throw e;
    }
  }

  /**
   * 本人の承認ステータスを更新します
   * @param number
   * @returns
   */
  public async agreement(req: KoyouKeiyakuAgreementReq) {
    try {
      const res = await this.post<boolean>(Config.API_URL + this.API_PREFIX + `agreement`, req)
      return res.data;
    } catch (e: any) {
      throw e;
    }
  }

  /**
   * 履歴書のURLを返却します
   * @param number
   * @returns
   */
  public async getRirekisyoUrl(id: number) {
    try {
      const res = await this.post<string>(Config.API_URL + this.API_PREFIX + `rirekisyo`, { targetId: id })
      return res.data;
    } catch (e: any) {
      throw e;
    }
  }

  // 雇用契約の担当営業に設定できるユーザーを返します
  public async getKoyouKeiyakuEigyouUser(): Promise<Syain[]> {
    try {
      const res = await this.post<Syain[]>(Config.API_URL + this.API_PREFIX + 'eigyou_list', null);
      return res.data;
    } catch (e: any) {
      throw e
    }
  }

  // 雇用契約の担当営業に設定できるユーザーを返します（オプション値）
  public async getKoyouKeiyakuEigyouUserOptions(): Promise<BaseOptionWithoutCode[]> {
    try {
      const res = await this.post<BaseOptionWithoutCode[]>(Config.API_URL + this.API_PREFIX + 'options/eigyou_list', null);
      return res.data;
    } catch (e: any) {
      throw e
    }
  }
}
